import { useEffect, useRef } from 'react';
import { AgentChatStatus } from '../../components/agent_chat/useAgentChat';
import { AgentPageContext } from '../../types/agentHarness';
import { OscarInvocation } from '../utils/OscarInvocation';

interface OscarInvocationChat {
	status: AgentChatStatus;
	busy: boolean;
	sendMessage: (text: string, context?: AgentPageContext) => Promise<void>;
	newConversation: () => Promise<void>;
}

/**
 * Sends waiting `callOscar` questions one at a time, in order. Like the composer, it waits for the
 * chat to be ready and for Oscar to finish answering before sending the next. `sendMessage` sets
 * `busy` before it first awaits, so the render that drops the sent question already sees the chat
 * busy and holds the rest.
 *
 * A question that shares a record always starts its own chat rather than continuing whatever was
 * open: asking about an invoice from its page is a new subject. Always, not "unless the chat is
 * empty": a resumed chat can read `ready` before its replay has landed, so emptiness is not a
 * reliable signal. `newConversation` reports `connecting` until the fresh chat is up, and the
 * question goes out when it is `ready` again. A question without a record continues the chat.
 */
const useOscarInvocationSender = (
	chat: OscarInvocationChat,
	next: OscarInvocation | undefined,
	onSent: () => void,
	contextFor: (invocation: OscarInvocation) => AgentPageContext | undefined
) => {
	// The question a fresh chat was started for, so it is started once, not on every ready.
	const startedFreshFor = useRef<OscarInvocation>(null);

	useEffect(() => {
		if (!next || chat.status !== 'ready' || chat.busy) return;
		const context = contextFor(next);
		if (context && startedFreshFor.current !== next) {
			startedFreshFor.current = next;
			chat.newConversation();
			return;
		}
		chat.sendMessage(next.prompt, context);
		onSent();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [next, chat.status, chat.busy]);
};

export default useOscarInvocationSender;
